/**
 * The arithmetic under the charts: a linear scale, round axis ticks from zero, line paths that
 * break where a value is missing, which x positions get a label, and which data point the pointer
 * is nearest. Pure (unit tested under plain Node); the components only draw.
 */

export type Scale = (value: number) => number;

/** Maps [d0, d1] onto [r0, r1]; a zero-width domain maps everything to r0. */
export function linear(d0: number, d1: number, r0: number, r1: number): Scale {
  const span = d1 - d0;
  return (value) => (span === 0 ? r0 : r0 + ((value - d0) / span) * (r1 - r0));
}

/** A round step near span / count: 1, 2, 2.5 or 5 times a power of ten. */
function niceStep(span: number, count: number): number {
  const raw = span / Math.max(1, count);
  const power = 10 ** Math.floor(Math.log10(raw));
  const unit = raw / power;
  return (unit <= 1 ? 1 : unit <= 2 ? 2 : unit <= 2.5 ? 2.5 : unit <= 5 ? 5 : 10) * power;
}

/**
 * Axis ticks from zero up to a round top at or above `max`, about `count` steps. Counts stay whole
 * (`integer`): a chart of 3 plays never ticks at 0.5. An empty or all-zero series still gets an axis.
 */
export function ticks(max: number, count = 4, integer = true): number[] {
  const top = Number.isFinite(max) && max > 0 ? max : 1;
  let step = niceStep(top, count);
  if (integer) step = Math.max(1, Math.ceil(step));
  const out: number[] = [];
  for (let v = 0; v < top + step / 2 && out.length <= count * 3; v += step) out.push(Number(v.toFixed(10)));
  if (out[out.length - 1]! < top) out.push(Number((out[out.length - 1]! + step).toFixed(10)));
  return out;
}

/** The largest finite value of any series (0 when there is none). */
export function maxOf(...series: ReadonlyArray<ReadonlyArray<number | null | undefined>>): number {
  let max = 0;
  for (const values of series) for (const v of values) if (typeof v === "number" && Number.isFinite(v) && v > max) max = v;
  return max;
}

/** An SVG path through the points; a missing value lifts the pen, so a gap is never drawn as zero. */
export function linePath(xs: readonly number[], ys: ReadonlyArray<number | null | undefined>): string {
  let d = "";
  let pen = false;
  ys.forEach((y, i) => {
    if (typeof y !== "number" || !Number.isFinite(y)) { pen = false; return; }
    d += `${pen ? "L" : "M"}${round(xs[i]!)} ${round(y)}`;
    pen = true;
  });
  return d;
}

/** The closed shape under a line, down to the baseline (for a wash under a retention curve). */
export function areaPath(xs: readonly number[], ys: readonly number[], baseline: number): string {
  if (!ys.length) return "";
  const top = ys.map((y, i) => `${i ? "L" : "M"}${round(xs[i]!)} ${round(y)}`).join("");
  return `${top}L${round(xs[xs.length - 1]!)} ${round(baseline)}L${round(xs[0]!)} ${round(baseline)}Z`;
}

/** The x position of each of `n` points across [start, end] (one point sits in the middle). */
export function positions(n: number, start: number, end: number): number[] {
  if (n <= 0) return [];
  if (n === 1) return [(start + end) / 2];
  const step = (end - start) / (n - 1);
  return Array.from({ length: n }, (_, i) => start + i * step);
}

/**
 * Which of `n` points get an x label: every `step`-th point from the first (a whole step, so labels
 * sit evenly), and the last; the one before the last gives way when the two would crowd.
 */
export function labelIndexes(n: number, max: number): number[] {
  if (n <= 0) return [];
  if (n <= max) return Array.from({ length: n }, (_, i) => i);
  const step = Math.ceil((n - 1) / (Math.max(2, max) - 1));
  const out: number[] = [];
  for (let i = 0; i < n - 1; i += step) out.push(i);
  if (out.length > 1 && n - 1 - out[out.length - 1]! < step * 0.6) out.pop();
  out.push(n - 1);
  return out;
}

/** The data point nearest to x among points at `xs` (sorted). */
export function nearestIndex(xs: readonly number[], x: number): number {
  if (!xs.length) return -1;
  let best = 0;
  for (let i = 1; i < xs.length; i++) if (Math.abs(xs[i]! - x) < Math.abs(xs[best]! - x)) best = i;
  return best;
}

const round = (n: number) => Math.round(n * 100) / 100;
